import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField,
} from '@mui/material';

/** Expiry choices. "Never" is the default: an unset expiry means the key lives until revoked. */
const EXPIRY_OPTIONS = [
  { value: '', label: 'Never expires' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
];

/**
 * Create a user API key (#1115). The caller handles the response, because the secret it returns can
 * only be shown once and belongs in ApiKeyRevealDialog.
 */
const ApiKeyCreateDialog = ({ open, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setName('');
    setReadOnly(false);
    setExpiresInDays('');
    setError(null);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onCreate({
        name: name.trim(),
        read_only: readOnly,
        ...(expiresInDays ? { expires_in_days: Number(expiresInDays) } : {}),
      });
      reset();
    } catch (e) {
      // The limit (409) and the entitlement gate (403) both surface here as actionable messages.
      setError(e.response?.data?.error?.message || e.message || 'Failed to create API key');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create API key</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="What will use this key, e.g. “CI publisher” or “transit pipeline”."
            inputProps={{ maxLength: 60 }}
            autoFocus
            fullWidth
          />

          <FormControlLabel
            control={<Checkbox checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} />}
            label="Read-only (GET requests only)"
          />

          <FormControl fullWidth>
            <InputLabel>Expiry</InputLabel>
            <Select
              label="Expiry"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
            >
              {EXPIRY_OPTIONS.map((o) => (
                <MenuItem key={String(o.value)} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info">
            The secret is shown once, immediately after creation, and cannot be retrieved later.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !name.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyCreateDialog;
